import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { useNavigate, useLocation } from 'react-router';
import * as jwt from '../../actions/jwt';
import { authorizeAPI, userAPI, settingAPI, commanderAPI } from '../../actions/imsServer';
import VectorIcon from '../../assets/images/Vector.svg';
import logoImage from '../../assets/images/heart-kium-logo.png';
import layer1Image from '../../assets/images/Layer_1.png';
import checkOffImage from '../../assets/images/check/off.png';
import checkOnImage from '../../assets/images/check/on.png';
import closeIcon from '../../assets/images/close.png';
import googleIcon from '../../assets/images/google.png';
import appleIcon from '../../assets/images/apple.png';
import messageIcon from '../../assets/images/message.png';
import phoneIcon from '../../assets/images/phone.png';

import { useDispatch } from 'react-redux';
import { setAuth } from '../../redux/infoSlice';
import UseDeviceInfo from '../../pages/commanders/UseDeviceInfo';
import Popup from '../popup';
import CustomSelect from '../select';

const StyledButton = styled(Button)({
  backgroundColor: '#FF43AA',
  color: 'white',
  borderRadius: '16px',
  height : '38px',
  padding: '12px', 
  '&.Mui-disabled': {
    backgroundColor: '#E0E0E0',
    color: '#9E9E9E',
  },
  textTransform: 'none',
  fontSize: '16px',
  marginTop: '16px'
});

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    borderRadius: '16px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    transition: 'all 0.2s',
    display: 'flex',
    height: '38px',
    padding: '0px 20px',
    alignItems: 'center',
    gap: '10px',
    alignSelf: 'stretch',
    '&.Mui-focused': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
      border: '1px solid rgba(0, 0, 0, 0.10)',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      padding: '0',
      height: '100%',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        transition: 'background-color 9999s ease-in-out 0s',
        WebkitTextFillColor: '#000000 !important',
        WebkitBoxShadow: 'none !important',
        background: 'none !important',
        backgroundColor: 'transparent !important'
      }
    }
  },
  '& .MuiInputBase-input': {
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiInputAdornment-root': {
      backgroundColor: 'transparent',
      marginRight: '8px',
      height: 'auto',
      '& .MuiIconButton-root': {
        color: '#757575',
        padding: '8px',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    }
  },
  '& input[type="password"]': {
    '&::-ms-reveal, &::-ms-clear': {
      display: 'none'
    }
  },
  // helperText가 있을 때도 높이 유지
  '& .MuiFormHelperText-root': {
    margin: '4px 0 0 0',
    fontSize: '12px',
    lineHeight: '16px',
    minHeight: '16px'
  }
});

const CustomCheckbox = ({ checked, onChange }) => (
  <Box
    component="img"
    src={checked ? checkOnImage : checkOffImage}
    alt="checkbox"
    sx={{
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      marginRight: '8px'
    }}
    onClick={() => onChange(!checked)}
  />
);

export const SignIn = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // location state에서 전화번호 로그인으로 돌아왔는지 확인
  const fromPasswordReset = location.state?.fromPasswordReset;
  const resetLoginType = location.state?.loginType;

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [loginError, setLoginError] = React.useState('');  
  const [activeTab, setActiveTab] = React.useState('user');
  const [loginType, setLoginType] = React.useState('email');
  const [countryCode, setCountryCode] = React.useState('81');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneError, setPhoneError] = React.useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');
  const [pushToken, setPushToken] = React.useState('');
  const [platform, setPlatform] = React.useState('web');
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [popupMessage, setPopupMessage] = React.useState('');
  const [pendingSNSLinkData, setPendingSNSLinkData] = React.useState(null); // 팝업 확인 후 처리할 SNS 연동 데이터
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'error' });
  const { deviceInfo } = UseDeviceInfo();

  // 비밀번호 유효성 검사 함수를 먼저 정의
  const validatePassword = (password) => {
    // activeTab이 user인 경우 6자리~12자리이기만 하면 됨
    if (activeTab === 'user') {
      return password.length >= 6 && password.length <= 12;
    }
    
    // admin 모드일 때는 기존 규칙 적용 (6-12자리, 2개 이상 카테고리)
    if (password.length < 6 || password.length > 12) {
      return false;
    }
    
    let categories = 0;
    if (/[a-zA-Z]/.test(password)) categories++; // 영문자
    if (/[0-9]/.test(password)) categories++; // 숫자
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) categories++; // 기호
    
    return categories >= 2; // 2개 이상의 카테고리 포함
  };

  // 버튼 상태를 useMemo로 계산
  const isButtonDisabled = useMemo(() => {
    const isValidEmail = /\S+@\S+\.\S+/.test(email);
    const isValidIdPhone = email.length >= 7 && /^\d+$/.test(email);
    const isValidPassword = validatePassword(password);
    const isValidPhone = phoneNumber.length >= 7 && /^\d+$/.test(phoneNumber);
    
    // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
    const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                       (localStorage.getItem('appType') === 'user' && activeTab === 'admin');
    
    let isIdValid = false;
    if (isAdminMode) {
      // admin 모드: 이메일 또는 전화번호 형식 모두 허용
      isIdValid = isValidEmail || isValidIdPhone;
    } else {
      // user 모드: loginType에 따라 검사
      if (loginType === 'email') {
        isIdValid = isValidEmail;
      } else {
        // 전화번호 로그인인 경우 phoneNumber 필드 검사
        isIdValid = isValidPhone;
      }
    }
    
    return !(isIdValid && isValidPassword);
  }, [email, password, phoneNumber, loginType, activeTab]);

  const handleClearEmail = () => setEmail('');

  const dispatch = useDispatch();

  // 자동완성 입력 감지를 위한 useEffect 추가
  useEffect(() => { 
    const emailInput = document.getElementById('email');
    const passwordInput = document.getElementById('password');

    if (emailInput && passwordInput) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const emailValue = emailInput.value;
            const passwordValue = passwordInput.value;
            
            if (emailValue && passwordValue) {
              const isValidEmail = /\S+@\S+\.\S+/.test(emailValue);
              const isValidIdPhone = emailValue.length >= 7 && /^\d+$/.test(emailValue);
              const isValidPassword = validatePassword(passwordValue);
              
              // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
              const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                                 (localStorage.getItem('appType') === 'user' && activeTab === 'admin');
              
              let isIdValid = false;
              if (isAdminMode) {
                // admin 모드: 이메일 또는 전화번호 형식 모두 허용
                isIdValid = isValidEmail || isValidIdPhone;
              } else {
                // user 모드: 이메일 형식만 검사
                isIdValid = isValidEmail;
              }
              
              setEmail(emailValue);
              setPassword(passwordValue);
              // 버튼 상태는 useMemo에서 자동으로 계산됨
              
              if (isValidEmail) {
                setEmailError(false);
                setEmailErrorMessage('');
              }
              
              if (isValidPassword) {
                setPasswordError(false);
                setPasswordErrorMessage('');
              }
            }
          }
        });
      });

      observer.observe(emailInput, { attributes: true });
      observer.observe(passwordInput, { attributes: true });

      return () => {
        observer.disconnect();
      };
    }
  }, [activeTab]);

  useEffect(() => {
    const isValidEmail = /\S+@\S+\.\S+/.test(email);
    const isValidIdPhone = email.length >= 7 && /^\d+$/.test(email);
    const isValidPassword = validatePassword(password);
    const isValidPhone = phoneNumber.length >= 7 && /^\d+$/.test(phoneNumber);
    
    // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
    const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                       (localStorage.getItem('appType') === 'user' && activeTab === 'admin');
    
    // 이메일 유효성 검사 (admin 모드에서는 전화번호 형식도 허용)
    if (email) {
      if (isAdminMode) {
        // admin 모드: 이메일 또는 전화번호 형식 모두 허용
        const isValidId = isValidEmail || isValidIdPhone;
       
        if (!isValidId) {
          setEmailError(true);
          setEmailErrorMessage(t('auth_invalid_id'));
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
      } else {
        // user 모드: 이메일 형식만 검사
        if (!isValidEmail) {
          setEmailError(true);
          setEmailErrorMessage(t('auth_invalid_email'));
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
      }
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    // 비밀번호 유효성 검사
    if (password && !isValidPassword) {
      setPasswordError(true);
      setPasswordErrorMessage(t('auth_invalid_password'));
    } else if (password) {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    // 전화번호 유효성 검사 (user 모드에서만)
    if (!isAdminMode && phoneNumber && !isValidPhone) {
      setPhoneError(true);
      setPhoneErrorMessage(t('auth_invalid_phone'));
    } else if (!isAdminMode && phoneNumber) {
      setPhoneError(false);
      setPhoneErrorMessage('');
    }
  }, [email, password, phoneNumber, loginType, activeTab, t]);

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    
    // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
    const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                       (localStorage.getItem('appType') === 'user' && activeTab === 'admin');
    
    if (!value) {
      setEmailError(false);
      setEmailErrorMessage('');
    } else if (isAdminMode) {
      // admin 모드: 이메일 또는 전화번호 형식 모두 허용
      const isValidEmail = /\S+@\S+\.\S+/.test(value);
      const isValidPhone = value.length >= 7 && /^\d+$/.test(value);
      
      if (!isValidEmail && !isValidPhone) {
        setEmailError(true);
        setEmailErrorMessage(t('auth_invalid_id'));
      } else {
        setEmailError(false);
        setEmailErrorMessage('');
      }
    } else {
      // user 모드: 이메일 형식만 검사
      if (!/\S+@\S+\.\S+/.test(value)) {
        setEmailError(true);
        setEmailErrorMessage(t('auth_invalid_email'));
      } else {
        setEmailError(false);
        setEmailErrorMessage('');
      }
    }
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    if (!value) {
      setPasswordError(false);
      setPasswordErrorMessage('');
    } else if (!validatePassword(value)) {
      setPasswordError(true);
      setPasswordErrorMessage(t('auth_invalid_password'));
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }
    
    // 버튼 상태는 useMemo에서 자동으로 계산됨
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
    
    // 전화번호 유효성 검사
    if (!value || value.length < 7) {
      setPhoneError(true);
      setPhoneErrorMessage(t('auth_invalid_phone'));
    } else {
      setPhoneError(false);
      setPhoneErrorMessage('');
    }
    
    // 버튼 상태는 useMemo에서 자동으로 계산됨
  };

  const validateInputs = async () => {
    let isValid = true;
    setLoginError(''); // 로그인 시도 시 에러 메시지 초기화

    // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
    const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                       (localStorage.getItem('appType') === 'user' && activeTab === 'admin');

    if (isAdminMode) {
      // admin 모드: 이메일 또는 전화번호 형식 모두 허용
      if (!email) {
        setEmailError(true);
        setEmailErrorMessage(t('auth_required_field'));
        isValid = false;
      } else {
          const isValidEmail = /\S+@\S+\.\S+/.test(email);
          const isValidPhone = email.length >= 7 && /^\d+$/.test(email);
          
          if (!isValidEmail && !isValidPhone) {
            setEmailError(true);
            setEmailErrorMessage(t('auth_invalid_id'));
            isValid = false;
          } else {
            setEmailError(false);
            setEmailErrorMessage('');
          }
        }
    } else {
      // user 모드: loginType에 따라 검사
      if (loginType === 'email') {
        // 이메일 로그인인 경우
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
          setEmailError(true);
          setEmailErrorMessage(t('auth_invalid_email'));
          isValid = false;
        }
      } else {
        // 전화번호 로그인인 경우
        if (!phoneNumber || phoneNumber.length < 7) {
          setPhoneError(true);
          setPhoneErrorMessage(t('auth_invalid_phone'));
          isValid = false;
        }
      }
    }

    if (!password || !validatePassword(password)) {
      setPasswordError(true);
      setPasswordErrorMessage(t('auth_invalid_password'));
      isValid = false;
    }

    if(isValid){
      setIsLoading(true);
      try {

        // appType이 user가 아니거나, appType이 user이면서 activeTab이 admin인 경우
        const isAdminMode = localStorage.getItem('appType') !== 'user' || 
                           (localStorage.getItem('appType') === 'user' && activeTab === 'admin');

        let username;
        if (isAdminMode) {
          // admin 모드: 이메일 또는 전화번호 형식 모두 허용
          if (/\S+@\S+\.\S+/.test(email)) {
            // 이메일 형식인 경우
            username = email;
          } else {
            // 전화번호 형식인 경우 (국가코드 추가)
            let formattedPhoneNumber = email; // 이미 숫자만 허용하므로 추가 정리 불필요
            if (formattedPhoneNumber.startsWith('0')) {
              formattedPhoneNumber = formattedPhoneNumber.substring(1);
            }
            username = countryCode + formattedPhoneNumber;
          }
        } else {
          // user 모드: loginType에 따라 처리
          if (loginType === 'email') {
            username = email;
          } else {
            // 국가코드와 전화번호 조합
            // 전화번호 맨 앞자리가 0인 경우 0 제외
            let formattedPhoneNumber = phoneNumber;
            if (phoneNumber.startsWith('0')) {
              formattedPhoneNumber = phoneNumber.substring(1);
            }
            username = countryCode + formattedPhoneNumber;
          }
        }

        const response = await authorizeAPI.signIn({
          grant_type: 'password',
          username: username,
          password: password,
          push_token: pushToken // Add push token to login request
        });
   
        if(response.access_token) {
          const appType = localStorage.getItem('appType');
          const decoded = jwt.decode(response.access_token);

          if((appType === 'admin' || appType === 'commander') && decoded.exp){
            setLoginError(t('auth_admin_only_access'));
            setEmailError(true);
            setPasswordError(true);
          }
          else{
            // appType이 user인 경우 탭별 권한 체크

            const userResponse = await userAPI.getUserMe(response.access_token);

            if(appType === 'user') {              
              const userGrade = userResponse.data.user.grade;
              
              // 사용자탭(activeTab === 'user')인 경우 grade가 1이어야 함
              if(activeTab === 'user' && userGrade !== 1) {
                setLoginError(t('auth_user_only_access'));
                setEmailError(true);
                setPasswordError(true);
                setIsLoading(false);
                return;
              }
              
              // 요양사탭(activeTab === 'admin')인 경우 grade가 1보다 커야 함
              if(activeTab === 'admin' && userGrade <= 1) {
                setLoginError(t('auth_caregiver_only_access'));
                setEmailError(true);
                setPasswordError(true);
                setIsLoading(false);
                return;
              }
              
              // 권한 체크 통과 시 meInfo 저장
              localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
            } else {
              // appType이 user가 아닌 경우 기존 로직 유지            
              localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
            }

            localStorage.setItem('access_token', response.access_token);
            localStorage.setItem('refresh_token', response.refresh_token);

            dispatch(setAuth({
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
            }));

            // meInfo 변경 이벤트 발생
            window.dispatchEvent(new Event('meInfoChanged'));
              
            if (rememberMe) {
              localStorage.setItem('rememberedEmail', email);
            } else {
              localStorage.removeItem('rememberedEmail');
            }
            
            // if mobile app
            if(window.ReactNativeWebView) {
              if(appType === 'commander') {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'accessToken',
                    data: response.access_token
                }));

                const commanderResponse = await commanderAPI.getCommanders({uid : deviceInfo?.uniqueId, state : '0,2'});
              
                const commanders = commanderResponse.data?.commanders || [];

                if (commanders.length > 0) {
                  if(commanders[0].hospitalId !== userResponse.data.user.hospitalId) {
                    setPopupMessage(t('auth_commander_account_mismatch'));
                    setPopupOpen(true);
                    setEmailError(true);
                    setPasswordError(true);
                    setIsLoading(false);

                    // signout
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('meInfo');

                    localStorage.removeItem('commanderUID');
                    localStorage.removeItem('commanderInfo');

                    dispatch(setAuth({accessToken: null, refreshToken: null}));
                    
                    return;
                  }
                }
              }            
              else if(appType === 'user') {
                try {
                  const meInfo = JSON.parse(localStorage.getItem('meInfo'));
                  if (!meInfo) return;
  
                  const settingResponse = await settingAPI.getSettings({userId: meInfo.id});
                  // noticePush 값 추출
                  let noticePush = true;
                  if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                    const noticeSetting = settingResponse.data.settings.find(s => s.name === 'noticePush');
                    noticePush = !(noticeSetting && (noticeSetting.value === false || noticeSetting.value === 'false'));
                  }
                  let activePush = true;
                  if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                    const activeSetting = settingResponse.data.settings.find(s => s.name === 'activePush');
                    activePush = !(activeSetting && (activeSetting.value === false || activeSetting.value === 'false'));
                  }
                  if (noticePush && activePush) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                      type: 'subscribeTopics',
                    }));
                  }
                } catch (e) {
                  // fallback: 기존 동작 유지 (원하면 생략 가능)
                  // window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'subscribeTopics' }));
                }
              }
            }
            
            // Commander 계정 불일치가 없을 때만 navigate 실행
            navigate(`/?appType=${appType}`, { replace: true });
          }
        }
        else {
          setLoginError(isAdminMode ? t('auth_sign_in_error_id') : loginType === 'email' ? t('auth_sign_in_error_email') : t('auth_sign_in_error_phone'));
          setEmailError(true);
          setPasswordError(true);
        }
      } catch (error) {
        setLoginError(isAdminMode ? t('auth_sign_in_error_id') : loginType === 'email' ? t('auth_sign_in_error_email') : t('auth_sign_in_error_phone'));
        setEmailError(true);
        setPasswordError(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // SNS 계정 연동 처리 함수 (팝업 확인 후 호출)
  const handleSNSLink = useCallback(async (loginData) => {
    setIsLoading(true);
    setPopupOpen(false);
    
    try {
      const registerData = {
        snsType: loginData.snsType,
        snsId: loginData.snsId,
        loginId: loginData.email,
        name: loginData.name,
      };

      const registerResponse = await userAPI.registerUser(registerData);
      
      // 연동 성공 후 자동 로그인 시도
      if (registerResponse) {
        // SNS 로그인 재시도
        const retryResponse = await authorizeAPI.signIn({
          grant_type: 'sns',
          sns_type: loginData?.snsType,
          sns_id: loginData?.snsId,
          push_token: pushToken
        });

        if (retryResponse?.access_token) {
          const appType = localStorage.getItem('appType');
          const decoded = jwt.decode(retryResponse.access_token);

          if ((appType === 'admin' || appType === 'commander') && decoded.exp) {
            setLoginError(t('auth_admin_only_access'));
            setEmailError(true);
            setPasswordError(true);
          } else {
            localStorage.setItem('access_token', retryResponse.access_token);
            localStorage.setItem('refresh_token', retryResponse.refresh_token);

            dispatch(setAuth({
              accessToken: retryResponse.access_token,
              refreshToken: retryResponse.refresh_token,
            }));

            if (appType === 'commander' && window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'accessToken',
                data: retryResponse.access_token
              }));
            }

            const userResponse = await userAPI.getUserMe(retryResponse.access_token);
            localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
            window.dispatchEvent(new Event('meInfoChanged'));

            // Send user grade information to WebView
            if (window.ReactNativeWebView) {
              try {
                const meInfo = JSON.parse(localStorage.getItem('meInfo'));
                if (meInfo) {
                  const settingResponse = await settingAPI.getSettings({ userId: meInfo.id });
                  let noticePush = true;
                  if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                    const noticeSetting = settingResponse.data.settings.find(s => s.name === 'noticePush');
                    noticePush = !(noticeSetting && (noticeSetting.value === false || noticeSetting.value === 'false'));
                  }
                  let activePush = true;
                  if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                    const activeSetting = settingResponse.data.settings.find(s => s.name === 'activePush');
                    activePush = !(activeSetting && (activeSetting.value === false || activeSetting.value === 'false'));
                  }
                  if (noticePush && activePush) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                      type: 'subscribeTopics',
                    }));
                  }
                }
              } catch (e) {
                console.error('Error setting up push notifications:', e);
              }
            }

            navigate(`/?appType=${appType}`, { replace: true });
            setIsLoading(false);
            return;
          }
        } else {
          // 재시도 로그인 실패
          console.error('SNS login retry failed - no access token');          
          setLoginError('계정 연동 후 로그인에 실패했습니다. 다시 시도해주세요.');
          setEmailError(true);
          setPasswordError(true);
          setIsLoading(false);
          return;
        }
      } else {
        // registerUser 응답이 없는 경우
        const errMsg = 'registerUser failed - no response';
        console.error(errMsg);
        setLoginError('계정 연동 중 오류가 발생했습니다. 다시 시도해주세요.');
        // setSnackbar({ open: true, message: errMsg, severity: 'error' });
        setEmailError(true);
        setPasswordError(true);
        setIsLoading(false);
        return;
      }
    } catch (registerError) {
      // const errMsg = registerError?.response?.data?.message
      //   || registerError?.message
      //   || (registerError?.response?.data && JSON.stringify(registerError.response.data))
      //   || String(registerError);
      console.error('Error registering user for SNS linking:', registerError);
      setLoginError('계정 연동 중 오류가 발생했습니다. 다시 시도해주세요.');
      // setSnackbar({ open: true, message: errMsg, severity: 'error' });
      setEmailError(true);
      setPasswordError(true);
      setIsLoading(false);
      return;
    }
  }, [pushToken, t, dispatch, navigate]);

  // SNS 로그인 처리 함수
  const handleSNSLogin = useCallback(async (loginData) => {       
    setIsLoading(true);
    setLoginError('');

    try {      
      // 구글 로그인을 위한 별도 API 호출
      const response = await authorizeAPI.signIn({
        grant_type: 'sns',
        sns_type:loginData?.snsType, // SNS provider type (kakao, naver, google, apple)
        sns_id: loginData?.snsId, // SNS id
        push_token: pushToken
      });

      if(response?.access_token) {        
        const appType = localStorage.getItem('appType');
        const decoded = jwt.decode(response.access_token);

        if((appType === 'admin' || appType === 'commander') && decoded.exp){          
          setLoginError(t('auth_admin_only_access'));
          setEmailError(true);
          setPasswordError(true);
        } else {          
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);

          dispatch(setAuth({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
          }));
          
          if(appType === 'commander' && window.ReactNativeWebView)
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'accessToken',
              data: response.access_token
          }));

          const userResponse = await userAPI.getUserMe(response.access_token);
          localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
          // meInfo 변경 이벤트 발생
          window.dispatchEvent(new Event('meInfoChanged'));            
          
          // Send user grade information to WebView
          if (window.ReactNativeWebView) {
            try {
              const meInfo = JSON.parse(localStorage.getItem('meInfo'));
              if (!meInfo) return;

              const settingResponse = await settingAPI.getSettings({userId: meInfo.id});
              // noticePush 값 추출
              let noticePush = true;
              if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                const noticeSetting = settingResponse.data.settings.find(s => s.name === 'noticePush');
                noticePush = !(noticeSetting && (noticeSetting.value === false || noticeSetting.value === 'false'));
              }
              let activePush = true;
              if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                const activeSetting = settingResponse.data.settings.find(s => s.name === 'activePush');
                activePush = !(activeSetting && (activeSetting.value === false || activeSetting.value === 'false'));
              }
              if (noticePush && activePush) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  type: 'subscribeTopics',
                }));
              }
            } catch (e) {
              console.error('Error setting up push notifications:', e);
            }
          }

          navigate(`/?appType=${appType}`, { replace: true });
        }
      }
    } catch (error) {
      console.log('SNS login error:', error);
      // 웹뷰에서도 에러 확인할 수 있도록 콘솔 로그 전송
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'console',
          data: ['SNS login error:', error]
        }));
      }
      
      // SNS 로그인 실패 시 계정 존재 여부 확인
      try {
        // loginData에서 email을 추출하여 계정 존재 여부 확인
        const checkEmail = loginData?.email;
        if (checkEmail) {
          const checkResponse = await userAPI.checkUserExists({ loginId: checkEmail });

          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'console',
              data: ['checkResponse:', checkResponse]
            }));
          }
          
          if (checkResponse.data.exists === true) {
            // 계정이 존재하는 경우 - 팝업 표시 후 registerUser 진행
            setIsLoading(false);
            setPopupMessage(t('auth_sns_account_linking_popup'));
            setPendingSNSLinkData(loginData); // 팝업 확인 후 처리할 데이터 저장
            setPopupOpen(true);
            return; // 팝업 확인 후 처리하도록 여기서 종료
          }
        }
      } catch (checkError) {
        console.error('Error checking user existence:', checkError);
        // 웹뷰에서도 에러 확인할 수 있도록 콘솔 로그 전송
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'console',
            data: ['Error checking user existence:', checkError]
          }));
        }
      }

      // // 계정이 없거나 확인 실패 시 회원가입
      // if(loginData.email) { // email이 있는 경우
      //   // 회원가입으로 이동 (step 2 = 두 번째 단계)
      //   navigate('/auth/signup', {state:{snsData: loginData, step: 2}});   
      // }else{ // email이 없는 경우
        // email이 없는 SNS(특히 iOS Apple 재로그인) 케이스:
        // 회원가입 페이지로 이동하지 않고 바로 가입 → SNS 로그인 진행
        try {
          const loginId = loginData?.email?.includes('@privaterelay.appleid.com') ? null : loginData.email ? loginData.email : null;
          const snsType = loginData?.snsType;
          const snsId = loginData?.snsId;

          // registerUser는 name이 필수. 임시값 사용          
          const incomingName = (loginData?.name && String(loginData.name).trim()) ? String(loginData.name).trim() : '';
          const name = incomingName || `SNS User ${Math.floor(Math.random() * 9000 + 1000)}`; // 4 digits

          try {
            await userAPI.registerUser({
              loginId,
              name,
              snsType,
              snsId
            });
          } catch (registerError) {
            const msg = registerError?.message || String(registerError);
            // 이미 가입된 경우에도 SNS 로그인 재시도는 진행
            if (!msg.includes('already_exists')) {
              throw registerError;
            }
          }

          const retryResponse = await authorizeAPI.signIn({
            grant_type: 'sns',
            sns_type: snsType,
            sns_id: snsId,
            push_token: pushToken
          });

          if (retryResponse?.access_token) {
            const appType = localStorage.getItem('appType');
            const decoded = jwt.decode(retryResponse.access_token);

            if ((appType === 'admin' || appType === 'commander') && decoded.exp) {
              setLoginError(t('auth_admin_only_access'));
              setEmailError(true);
              setPasswordError(true);
              return;
            }

            localStorage.setItem('access_token', retryResponse.access_token);
            localStorage.setItem('refresh_token', retryResponse.refresh_token);

            dispatch(setAuth({
              accessToken: retryResponse.access_token,
              refreshToken: retryResponse.refresh_token,
            }));

            if(appType === 'commander' && window.ReactNativeWebView)
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'accessToken',
                data: retryResponse.access_token
            }));

            const userResponse = await userAPI.getUserMe(retryResponse.access_token);
            localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
            window.dispatchEvent(new Event('meInfoChanged'));

            navigate(`/?appType=${appType}`, { replace: true });
            return;
          }
          setLoginError(t('auth_sns_login_failed'));
        } catch (registerOrRetryError) {
          setLoginError(t('auth_sns_login_failed'));
        }
     //}
      
      setEmailError(true);
      setPasswordError(true);    
    } finally {          
      setIsLoading(false);
    }
  }, [pushToken, t, dispatch, navigate]);

  useEffect(() => {    
    // Notify Main.js that login page is loaded
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'pageLoaded'
      }));
    }

    // Get platform information from React Native
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'getPlatform'
      }));
    }

    // 저장된 이메일 불러오기
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
      // 이메일 유효성 검사
      if (/\S+@\S+\.\S+/.test(savedEmail)) {
        setEmailError(false);
        setEmailErrorMessage('');
      }
    }

    // appType이 admin이면 activeTab을 admin으로 설정
    const appType = localStorage.getItem('appType');
    if (appType !== 'user') {
      setActiveTab('admin');
    }
    
    // 비밀번호찾기에서 전화번호 로그인으로 돌아온 경우
    if (fromPasswordReset && resetLoginType === 'phone') {
      setLoginType('phone');
      setActiveTab('user');
    }

    // Listen for push token event
    const handlePushToken = (event) => {
      const token = event.detail;
      setPushToken(token);
    };
    

    // Listen for message events
    const handleMessage = (event) => {     
      try {
        // Skip webpack hot reload messages and other non-JSON messages
        if (typeof event.data === 'string' && (
          event.data.startsWith('webpackHot') || 
          event.data.startsWith('webpack') ||
          event.data.startsWith('{') === false
        )) {
          return;
        }
        
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;        

        if (data.type === 'googleLoginResult' || data.type === 'appleLoginResult') {          
          if (data.success && data.data) {
            // 구글/애플 로그인 성공 시 자동 로그인 처리
            const loginData = data.data;            
            
            // sns 로그인 처리 함수 호출
            handleSNSLogin(loginData);
          } else {
            // 구글/애플 로그인 실패      
            setLoginError(t('auth_sns_login_failed'));            
          }
        } else if (data.type === 'platformInfo') {
          // Set platform information received from React Native
          setPlatform(data.platform);
        } else if (data.type && data.error) {
          setLoginError(data.error || (activeTab === 'admin' ? t('auth_sign_in_error_id') : loginType === 'email' ? t('auth_sign_in_error_email') : t('auth_sign_in_error_phone')));        
          console.log('signIn : ' + data?.error);
        }
      } catch (e) {
        // Only log errors for actual JSON parsing issues, not webpack messages
        if (!event.data || typeof event.data !== 'string' || !event.data.startsWith('webpack')) {
          console.error('Error handling message:', e);
        }
      }
    };

    // Add event listeners
    window.addEventListener('pushTokenReceived', handlePushToken);
    window.addEventListener('message', handleMessage);
    
    // Cleanup
    return () => {
      window.removeEventListener('pushTokenReceived', handlePushToken);
      window.removeEventListener('message', handleMessage);
    };
  }, [t, handleSNSLogin, fromPasswordReset, resetLoginType]);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'center',
        alignItems: 'center',        
        bgcolor: { xs: '#FFF', sm: '#FDFBFE' },        
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,        
        overflow: 'hidden'     
      }}
    >
      <Box 
        sx={{
        display: 'flex',
        width: { xs: '100%', sm: '379px' },
        padding: { xs: '0px 0px 40px 0px', sm: '32px' },
        flexDirection: { xs: 'column', sm: 'column' },
        justifyContent: { xs: 'center', sm: 'center' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexShrink: { xs: '1', sm: '0' },
        borderRadius: '20px',
        background: '#FFF'
      }}>
        {/* 데스크탑 버전 로고 영역 */}
        <Box sx={{
         display: { xs: 'none', sm: 'flex' },
         padding: '0 10px 10px 88px',
         justifyContent: 'flex-end',
         alignItems: 'center',
         gap: '57px',
         alignSelf: 'stretch',
        }}>
          <Box
            component="img"
            src={logoImage}
            alt="Heart Kium Logo"
            sx={{
              width: 145,
              height: 30,
              aspectRatio: '29/6',
              display: { xs: 'none', sm: 'block' }
            }}
          />
          <IconButton
            onClick={() => navigate('/auth/language')}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              width: 20,
              height: 20,
              aspectRatio: '1/1'
            }}
          >
            <Box
              component="img"
              src={VectorIcon}
              alt="Language"
              sx={{
                width: 16,
                height: 16,
                flexShrink: 0,
                aspectRatio: '1/1'
              }}
            />
          </IconButton>
        </Box>         
        <Box
          sx={{
            display: 'flex',
            padding: { xs: '0px 20px', sm: 'auto' },
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-start' },
            alignItems: 'center',         
            alignSelf: { xs: 'stretch', sm: 'auto' },        
            overflow: { xs: 'auto', sm: 'auto' }
          }}
        >
          {/* 모바일 버전 로고 영역 */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            mb: 2,
            position: 'relative',
          }}>            
            <Box
              component="img"
              src={logoImage}
              alt="Heart Kium Logo"
              sx={{
                width: 145,
                height: 30,
                aspectRatio: '29/6',
                display: { xs: 'block', sm: 'none' }
              }}
            />
            <IconButton
              onClick={() => navigate('/auth/language')}
              sx={{
                display: { xs: 'flex', sm: 'none' },
                position: 'absolute',
                right: 10,
                width: 20,
                height: 20,
                aspectRatio: '1/1'
              }}
            >
              <Box
                component="img"
                src={VectorIcon}
                alt="Language"
                sx={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  aspectRatio: '1/1'
                }}
              />
            </IconButton>
          </Box>        
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: { xs: '10px', sm: '20px' },
              alignSelf: 'stretch'
            }}
          >
            {/* 데스크탑 버전 로그인 타이틀 */}
            <Typography
              variant="h5"
              sx={{
                color: '#211F20',
                textAlign: 'center',
                fontSize: '30px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',          
                alignSelf: 'stretch',                  
                display: { xs: 'none', sm: 'block' }
              }}
            >
            {t('auth_sign_in')}
            </Typography>
            <Box sx={{
              width: '315px',
              height: '1px',
              background: 'rgba(0, 0, 0, 0.10)',
              display: { xs: 'none', sm: 'block' }
            }}/>
            {/* 사용자/관리자용 탭 */}
            <Box sx={{
              display: localStorage.getItem('appType') === 'user' ? 'flex' : 'none',            
              height: '40px',
              justifyContent: 'center',
              alignItems: 'flex-end',
              alignSelf: 'stretch',
              borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
              mb: { xs: '10px', sm: '0px' }
            }}>
              <Box 
                sx={{
                  display: 'flex',
                  minWidth: '100px',
                  maxWidth: '200px',
                  paddingBottom: '10px',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  gap: '10px',
                  flex: '1 0 0',
                  alignSelf: 'stretch',
                  borderBottom: activeTab === 'user' ? '4px solid #FF43AA' : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setActiveTab('user');
                  // loginType은 변경하지 않음 - 사용자가 선택한 로그인 방식 유지
                }}
              >
                  <Typography sx={{
                  color: activeTab === 'user' ? '#FF43AA' : '#211F20',
                  textAlign: 'center',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px'
                }}>
                  {t('menu_users')}･{t('guardian')}
                </Typography>
              </Box>
              <Box 
                sx={{
                  display: 'flex',
                  minWidth: '100px',
                  maxWidth: '200px',
                  paddingBottom: '10px',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  gap: '10px',
                  flex: '1 0 0',
                  alignSelf: 'stretch',
                  borderBottom: activeTab === 'admin' ? '4px solid #FF43AA' : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setActiveTab('admin');
                  // loginType은 변경하지 않음 - 사용자가 선택한 로그인 방식 유지
                }}
              >
                <Typography sx={{
                  color: activeTab === 'admin' ? '#FF43AA' : '#211F20',
                  textAlign: 'center',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px'
                }}>
                  {t('menu_admins')}
                </Typography>
              </Box>   
            </Box>         
            {/* 입력폼 */}
            <Box
              component="form"
              noValidate
              sx={{
                //display: { xs: 'flex' },
                width: '100%',
                height: '250px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: { xs: '10px' },
                alignSelf: 'stretch'
              }}
            >
              {/* 이메일 로그인 */}
              {loginType === 'email'  ?     
              <Box sx={{ width: '100%', height: '170px', flexDirection: 'column', alignItems: 'center', gap: '8px', alignSelf: 'stretch' }}>
                <Box sx={{ width: '100%', height: activeTab === 'user' ? '50px' : '80px'}}>
                <FormControl sx={{ 
                  display: activeTab === 'user' ? 'none' : 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',              
                  alignSelf: 'stretch',
                }}>
                    <Typography
                        sx={{              
                          color: { xs: 'var(--text-subtitle, rgba(0, 0, 0, 0.60))', sm: '#211F20' },
                          textAlign: 'center',
                          fontSize: '15px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal'
                        }}
                      >
                        {t('country_region')}
                    </Typography>               
                    <CustomSelect
                        name="country"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        options={[
                          { value: '81', label: t('japan_code') },                      
                          { value: '886', label: t('taiwan_code') },
                          { value: '62', label: t('indonesia_code') },
                          { value: '82', label: t('korea_code') }
                        ]}   
                        height="38px"
                        borderRadius="8px"
                        fontSize="15px"
                        padding="10px 20px"
                        backgroundColor="#FFFFFF"
                        required
                      />
                    <Box sx={{ width: '100%', height: '16px' }}>
                      {/* 에러 메시지 영역 (국가 선택은 에러가 없으므로 빈 공간) */}
                    </Box>
                </FormControl>     
                </Box>
              
                <FormControl sx={{ 
                  display: { xs: 'flex', sm: 'block' },
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch',
                  minHeight: { xs: '70px', sm: '80px' } // 모바일에서 높이 줄임
                }}>
                  <Typography
                    sx={{              
                      color: loginType === 'email' ? '#211F20' : 'rgba(0, 0, 0, 0.60)', 
                      textAlign: 'center',
                          fontSize: '15px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal'
                    }}
                  >
                    { localStorage.getItem('appType') === 'user' && activeTab === 'user' ? t('user_email') : t('form_id')}
                  </Typography>
                  <StyledTextField
                    error={emailError}
                    helperText="" // helperText를 비워두고 별도로 처리
                    id="email"
                    type="email"
                    name="email"
                    placeholder={localStorage.getItem('appType') === 'user' && activeTab === 'user' ? t('form_email_placeholder') : t('form_id_placeholder')}
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={handleEmailChange}
                    sx={{
                      height: 38,
                      minHeight: 38,
                      padding: '0px 16px',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      alignSelf: 'stretch',
                      color: 'rgba(0, 0, 0, 0.30)',
                      textAlign: 'center',
                      fontSize: '15px',
                      fontStyle: 'normal',
                      fontWeight: '500',
                      lineHeight: 'normal'
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end"   sx={{ minWidth: '20px', minHeight: '20px', aspectRatio: '1/1' }}>
                          {email ? (
                            <IconButton onClick={handleClearEmail} edge="end" size="small">
                              <Box
                                component="img"
                                src={closeIcon}
                                sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                              />
                            </IconButton>
                          ) : (
                            <Box sx={{ width: '20px', height: '20px' }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ width: '100%', height: '12px' }}>
                    {/* 별도의 에러 메시지 영역 */}
                    {emailError && !loginError && (
                      <Typography
                        sx={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          lineHeight: '14px',
                          minHeight: '14px',
                          textAlign: 'center',
                              margin: 0
                        }}
                      >
                        {emailErrorMessage}
                      </Typography>
                    )}
                  </Box>
                </FormControl>
              </Box>
              : 
              <Box sx={{ width: '100%', height: '170px', flexDirection: 'column', alignItems: 'center', gap: '8px', alignSelf: 'stretch' }}>
              {/* 전화번호 로그인 */}
              <FormControl sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',              
                alignSelf: 'stretch',
                minHeight: { xs: '50px', sm: '80px' }
              }}>
                  <Typography
                      sx={{              
                        color: { xs: 'var(--text-subtitle, rgba(0, 0, 0, 0.60))', sm: '#211F20' },
                        textAlign: 'center',
                              fontSize: '15px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal'
                      }}
                    >
                      {t('country_region')}
                  </Typography>               
                  <CustomSelect
                      name="country"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      options={[
                        { value: '81', label: t('japan_code') },                      
                        { value: '886', label: t('taiwan_code') },
                        { value: '62', label: t('indonesia_code') },
                        { value: '82', label: t('korea_code') }
                      ]}            
                      sx={{                        
                        width: '283px',
                        height: '38px',
                        padding: '0px 20px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        alignSelf: 'center',
                        '& .MuiSelect-select': {
                          padding: '0',
                          textAlign: 'left'
                        }
                      }}
                      required
                    />
                  <Box sx={{ width: '100%', height: '12px' }}>
                    {/* 에러 메시지 영역 (국가 선택은 에러가 없으므로 빈 공간) */}
                  </Box>
              </FormControl>
              <FormControl sx={{ 
                  display: { xs: 'flex', sm: 'block' },
                  flexDirection: 'column',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  minHeight: { xs: '50px', sm: '80px' }
                }}>
                    <Typography
                      sx={{              
                        color: { xs: 'var(--text-subtitle, rgba(0, 0, 0, 0.60))', sm: '#211F20' },
                        textAlign: 'center',
                              fontSize: '15px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                        mb: 1
                      }}
                    >
                      {t('user_phone')}
                    </Typography>
                    <StyledTextField
                        error={phoneError}
                        helperText="" // helperText를 비워두고 별도로 처리
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder={t('auth_phone_placeholder')}
                        autoComplete="tel"
                        required
                        fullWidth
                        variant="outlined"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        sx={{
                          height: 38,
                          padding: '0px 16px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          color: 'rgba(0, 0, 0, 0.30)',
                          textAlign: 'center',
                                  fontSize: '15px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: 'normal'
                        }}
                      />
                    <Box sx={{ width: '100%', height: '12px' }}>
                    {/* 별도의 에러 메시지 영역 */}
                    {phoneError && !loginError && (
                      <Typography
                        sx={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          lineHeight: '14px',
                          minHeight: '14px',
                          textAlign: 'center',
                                  margin: 0
                        }}
                      >
                        {phoneErrorMessage}
                      </Typography>
                    )}
                    </Box>
              </FormControl>
              </Box>
              }
              <FormControl
                sx={{
                  display: { xs: 'flex', sm: 'block' },
                  flexDirection: { xs: 'column' },
                  alignItems: { xs: 'center' },
                  alignSelf: { xs: 'stretch' },
                  minHeight: { xs: '50px', sm: '80px' }
                }}
              >
                <Typography sx={{ 
                  color: { xs: 'var(--text-subtitle, rgba(0, 0, 0, 0.60))', sm: '#211F20' }, 
                  textAlign: 'center',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                  mb: 1
                }}>{t('form_password')}</Typography>
                <Box sx={{ display: 'flex', gap: '10px', width: '100%', mb: 1 }}>
                  <StyledTextField
                    error={passwordError}
                    helperText="" // helperText를 비워두고 별도로 처리
                    name="password"
                    placeholder="••••••"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                  //  autoComplete="current-password"
                    required
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={handlePasswordChange}
                    sx={{
                      display: 'flex',
                      height: 38,
                      padding: '0px 16px',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      alignSelf: 'stretch',
                      color: 'rgba(0, 0, 0, 0.30)',
                      textAlign: 'center',
                          fontSize: '15px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal'
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end" size="small"                     
                          >
                            <Box
                              component="img"
                              src={layer1Image}
                              alt="toggle password"
                              sx={{ 
                                display: 'flex',
                                width: '20px',
                                height: '20px',
                                padding: '5.625px 0.625px 4.879px 0.625px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                aspectRatio: '1/1'
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ width: '100%', height: '28px' }}>
                {/* 별도의 에러 메시지 영역 */}
                {passwordError && !loginError && (
                  <Typography
                    sx={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      lineHeight: '14px',
                      minHeight: '14px',
                      textAlign: 'center',
                          margin: 0
                    }}
                  >
                    {passwordErrorMessage}
                  </Typography>
                )}               
                {loginError && (
                  <Typography color="error" sx={{ fontSize: '12px',
                    lineHeight: '14px',
                    minHeight: '14px',
                    textAlign: 'center',
                        margin: 0 }}>
                  {loginError}
                  </Typography>
                )}   
                </Box>     
                <Typography
                  sx={{
                    color: 'rgba(0, 0, 0, 0.60)',
                    textAlign: 'center',
                    fontSize: '15px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textDecorationThickness: 'auto',
                    textUnderlineOffset: 'auto',
                    textUnderlinePosition: 'from-font',
                    display: 'block'
                  }}
                  onClick={() => navigate('/auth/reset-password', {state: {type : 'findPassword', email: email, loginType: loginType}})}
                >
                  {activeTab !== 'admin' && t('auth_forgot_password')}
                </Typography>              
              </FormControl>
            </Box>
            <Box sx={{              
                    width: '320px',
                    height: '40px',   
                  }}>
              <Box 
                  sx={{              
                    width: '320px',
                    height: '40px',
                    padding: '0px 20px',
                    alignItems: 'center',
                    gap: '10px',
                    display: activeTab === 'admin' ? 'flex' : 'none'
                  }}
                >
                  <CustomCheckbox
                    checked={rememberMe}
                    onChange={(checked) => setRememberMe(checked)}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#666666',
                      cursor: 'pointer'
                    }}
                    onClick={() => setRememberMe(!rememberMe)}
                  >
                    {t('remember_me')}
                  </Typography>
              </Box>
            </Box>
            
          </Box>      
        </Box>
        {/* 하단 영역 */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          alignItems: 'center',
          alignSelf: 'stretch',
          mt: { xs: '0px', sm: '0px' },            
        }}>          
          <Box sx={{
            display: 'flex',
            padding: '0px 20px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '15px', sm: '20px' },
            alignSelf: 'stretch'
          }}>
            {/* 로그인 버튼*/}
            <StyledButton 
              fullWidth
              type="submit"
              disabled={isButtonDisabled || isLoading}
              onClick={() => {            
                if (!isButtonDisabled && !isLoading) {
                  validateInputs();
                }
              }}
              sx={{
                mt: { xs: '0px', sm: '0px' },
                display: 'flex',
                height: '38px',
                padding: '10px 30px',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'stretch'
              }}
            >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              t('auth_sign_in')
            )}
            </StyledButton>   
            <Box sx={{ width: '100%', height: '100px' }}>
              {activeTab === 'user' && (
              <>
                {/* 로그인 타입 토글 버튼 */}
                <Typography
                  sx={{
                    color: 'rgba(0, 0, 0, 0.60))',
                    textAlign: 'center',
                    fontSize: '15px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal'
                  }}
                >
                  {t('auth_other_login_methods')}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '40px',
                  alignSelf: 'stretch',
                  padding: '10px 20px'
                }}>
                  {/* 로그인 타입 토글 버튼 */}
                  <Box
                    component="img"
                    src={loginType === 'email' ? phoneIcon : messageIcon}
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      aspectRatio: 1/1, 
                      cursor: 'pointer',
                      opacity: 0.7,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={() => {
                      const newLoginType = loginType === 'email' ? 'phone' : 'email';
                      setLoginType(newLoginType);
                      
                      // 로그인 타입 변경 시 입력값 초기화
                      if (newLoginType === 'phone') {
                        // 전화번호 로그인으로 변경
                        setEmail('');
                        setEmailError(false);
                        setEmailErrorMessage('');
                      } else {
                        // 이메일 로그인으로 변경
                        setPhoneNumber('');
                        setPhoneError(false);
                        setPhoneErrorMessage('');
                      }
                    }}
                  />
                  {platform === 'android' && (
                    <Box
                        component="img"
                        src={googleIcon}
                        sx={{ width: 50, height: 50, aspectRatio: 1/1, cursor: 'pointer' }}
                        onClick={() => {
                          if (window.ReactNativeWebView) {
                            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'googleLogin' }));
                          }
                        }}
                      />
                  )}
                  {platform === 'ios' && (
                    <Box
                      component="img"
                      src={appleIcon}
                      sx={{ width: 50, height: 50, aspectRatio: 1/1, cursor: 'pointer' }}
                      onClick={() => {
                        if (window.ReactNativeWebView) {
                          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'appleLogin' }));
                        }
                      }}
                    />
                  )}           
                </Box>
                {/* 회원가입 버튼 */}
                <Box sx={{
                  display: 'flex',
                  height: '18px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch'
                }} onClick={()=>navigate('/auth/signup', {state:{type:loginType}})}>
                  <Typography sx={{
                    color: 'rgba(0, 0, 0, 0.60)',
                    textAlign: 'center',
                      fontSize: '15px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal'
                  }}>
                    {t('auth_no_account')}
                  </Typography>
                  <Typography sx={{
                    color: '#FF43AA',
                    textAlign: 'center',
                      fontSize: '15px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textDecorationThickness: 'auto',
                    textUnderlineOffset: 'auto',
                    textUnderlinePosition: 'from-font'
                  }}>{t('auth_sign_up')}</Typography>
                </Box>
              </>
              )}
            </Box>       
          </Box>
        </Box>
      </Box>
      
      {/* Popup for commander account mismatch and SNS account linking */}
      <Popup
        open={popupOpen}        
        message={popupMessage}
        onConfirm={() => {
          if (pendingSNSLinkData) {
            // SNS 계정 연동 팝업인 경우
            handleSNSLink(pendingSNSLinkData);
            setPendingSNSLinkData(null);
          } else {
            // 일반 팝업인 경우 (commander account mismatch 등)
            setPopupOpen(false);
          }
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: snackbar.severity === 'error' ? '#FF6B6B' : undefined,
            color: '#FFFFFF',
            '& .MuiAlert-icon': { color: '#FFFFFF' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};